import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    PawPrint, Siren, MapPin, ShieldCheck, Heart, X,
    Phone, Eye, Crosshair, Compass,
} from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import GoogleCommunityMapView from "@/components/community/GoogleCommunityMapView";
import api, { fileUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useIsStoreApp } from "@/lib/storeAppDetection";
import { useHandlePremiumGate } from "@/lib/premiumGate";

const PLACEHOLDER_DOG = "https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=200&q=60";

/**
 * Region-based fallback center used when the user has NOT shared their approximate area yet.
 * The map still loads immediately — we simply pick a sensible regional overview based on the UI
 * language so the map feels contextual, then invite the user to add their exact area.
 * No fake data is ever rendered on this map — helpers/lost/found lists remain empty until real
 * data flows in from the DB.
 */
const DEFAULT_CENTER_BY_LANG = {
    ar: { latitude: 30.0444, longitude: 31.2357, zoom: 5 }, // Cairo — MENA regional overview
    fr: { latitude: 46.6, longitude: 2.5, zoom: 5 },        // France regional overview
    es: { latitude: 40.4, longitude: -3.7, zoom: 5 },       // Madrid — Iberian overview
    en: { latitude: 46.8, longitude: 8.2, zoom: 5 },        // Switzerland — CHF market fallback
};
const getFallbackCenter = (lang) => DEFAULT_CENTER_BY_LANG[lang] || DEFAULT_CENTER_BY_LANG.en;

/* -------------------------------------------------------------
 *  Rich bottom-sheet card
 * -----------------------------------------------------------*/
function PetSheet({ pet, onClose, t, kind }) {
    const [sightingStatus, setSightingStatus] = useState("idle");
    const sendSighting = () => {
        if (!navigator.geolocation) return;
        setSightingStatus("locating");
        navigator.geolocation.getCurrentPosition(async (pos) => {
            try {
                await api.post(`/public/pets/${pet.public_id}/sighting`, {
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    message: t("finder.sighting_default_msg", { name: pet.name }),
                });
                setSightingStatus("sent");
            } catch (_) { setSightingStatus("idle"); }
        }, () => setSightingStatus("idle"), { timeout: 8000, enableHighAccuracy: true });
    };

    const isLost = kind === "lost";
    return (
        <motion.div
            key="sheet"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[2000] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
            onClick={onClose}
            data-testid="community-pet-sheet"
        >
            <motion.div
                initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 26 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl bg-white dark:bg-[hsl(152,15%,10%)] shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
            >
                {/* Header photo — full aspect, biased to pet face */}
                <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] bg-gradient-to-br from-paw-muted to-paw-line dark:from-white/5 dark:to-white/10 overflow-hidden flex-shrink-0">
                    <img
                        src={pet.photo_path ? fileUrl(pet.photo_path) : PLACEHOLDER_DOG}
                        alt={pet.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: "center 30%" }}
                        loading="eager"
                    />
                    <div aria-hidden className={`absolute inset-0 pointer-events-none ${isLost
                        ? "bg-gradient-to-t from-red-950/70 via-red-900/10 to-transparent"
                        : "bg-gradient-to-t from-emerald-950/55 via-transparent to-transparent"}`} />
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="community-pet-sheet-close"
                        className="absolute top-3 end-3 z-10 w-9 h-9 rounded-full bg-black/50 backdrop-blur grid place-items-center text-white hover:bg-black/70 transition"
                        aria-label={t("common.close")}
                    >
                        <X size={16} weight="bold" />
                    </button>
                    <div className="absolute bottom-4 start-4 end-4">
                        <span
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold ${
                                isLost ? "bg-red-500 text-white" : "bg-emerald-500 text-white"
                            }`}
                        >
                            {isLost ? <Siren size={11} weight="fill" /> : <Heart size={11} weight="fill" />}
                            {isLost ? t("community.sheet_missing") : t("community.sheet_found_safe")}
                        </span>
                        <p className="mt-1.5 font-display text-2xl sm:text-3xl text-white leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]">
                            {pet.name}
                        </p>
                        {pet.breed && <p className="text-white/90 text-sm drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]">{pet.breed}</p>}
                    </div>
                </div>

                {/* Scrollable body */}
                <div className="p-5 sm:p-6 overflow-y-auto flex-1">
                    {typeof pet.distance_km === "number" && (
                        <p className="inline-flex items-center gap-1.5 text-sm text-paw-ink2 dark:text-paw-bone/70 mb-3">
                            <MapPin size={14} weight="fill" className="text-paw-forest dark:text-paw-terra" />
                            {t("community.distance_km", { km: pet.distance_km })}
                            {isLost && pet.last_seen && <span className="opacity-70">· {t("community.last_seen")}: {pet.last_seen}</span>}
                            {!isLost && pet.neighborhood && <span className="opacity-70">· {pet.neighborhood}</span>}
                        </p>
                    )}

                    {!isLost && (
                        <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 p-3.5 flex items-start gap-3">
                            <span className="text-2xl" aria-hidden>❤️</span>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">{t("community.sheet_reunion_h")}</p>
                                <p className="mt-0.5 text-[12px] text-emerald-800/85 dark:text-emerald-200/80 leading-snug">
                                    {t("community.sheet_reunion_sub", { name: pet.name })}
                                </p>
                            </div>
                        </div>
                    )}

                    {pet.story && isLost && (
                        <blockquote className="mt-3 rounded-2xl bg-paw-terra/6 dark:bg-paw-terra/12 border border-paw-terra/20 p-3.5 text-sm italic text-paw-ink dark:text-paw-bone leading-relaxed">
                            &ldquo;{pet.story}&rdquo;
                        </blockquote>
                    )}

                    {!isLost && (
                        <Link
                            to={`/p/${pet.public_id}`}
                            data-testid="community-sheet-view"
                            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3.5 shadow-soft transition-all duration-300 hover:-translate-y-0.5"
                        >
                            <PawPrint size={16} weight="fill" />
                            {t("community.view_pet")}
                        </Link>
                    )}

                    {isLost && (
                        <div className="mt-4 space-y-2.5">
                            <button
                                type="button"
                                onClick={sendSighting}
                                disabled={sightingStatus === "locating" || sightingStatus === "sent"}
                                data-testid="community-sheet-isaw"
                                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-70 text-white font-semibold px-5 py-3.5 shadow-soft transition-all duration-300 hover:-translate-y-0.5"
                            >
                                <Eye size={16} weight="fill" />
                                {sightingStatus === "locating" ? t("community.popup_sighting_sending")
                                    : sightingStatus === "sent" ? t("community.popup_sighting_sent")
                                        : `👀 ${t("community.popup_isaw")}`}
                            </button>
                            <Link
                                to={`/p/${pet.public_id}`}
                                data-testid="community-sheet-ifound"
                                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold px-5 py-3.5 shadow-soft transition-all duration-300 hover:-translate-y-0.5"
                            >
                                <PawPrint size={16} weight="fill" />
                                {t("community.popup_ifound")}
                            </Link>
                            {pet.owner_phone && (
                                <a
                                    href={`tel:${pet.owner_phone}`}
                                    data-testid="community-sheet-call"
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-paw-line dark:border-white/15 text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 font-semibold px-5 py-3.5 transition"
                                >
                                    <Phone size={16} weight="fill" />
                                    {t("community.sheet_contact_owner")}
                                </a>
                            )}
                        </div>
                    )}

                    <p className="mt-4 text-[10px] text-paw-ink2/70 dark:text-paw-bone/40 text-center">
                        🔒 {t("community.sheet_privacy")}
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
}

/* -------------------------------------------------------------
 *  Helper card popup (small anonymous card)
 * -----------------------------------------------------------*/
function HelperSheet({ helper, onClose, t }) {
    return (
        <motion.div
            key="helper-sheet"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-6"
            onClick={onClose}
            data-testid="community-helper-sheet"
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: "spring", stiffness: 240, damping: 22 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-xs rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] shadow-2xl overflow-hidden p-6 text-center"
            >
                <div className="relative w-24 h-24 rounded-full mx-auto overflow-hidden border-4 border-emerald-500 shadow-hover">
                    <img
                        src={helper.pet_photo_path ? fileUrl(helper.pet_photo_path) : PLACEHOLDER_DOG}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
                <p className="mt-3 text-lg font-display text-paw-ink dark:text-paw-bone">
                    {t("community.pin_helper_anon")}
                </p>
                {helper.neighborhood && (
                    <p className="text-xs text-paw-ink2 dark:text-paw-bone/60">
                        ≈ {helper.neighborhood}{typeof helper.distance_km === "number" ? ` · ${t("community.distance_km", { km: helper.distance_km })}` : ""}
                    </p>
                )}
                {/* Availability chip */}
                {helper.availability && (
                    <p className={`mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        helper.availability === "available"
                            ? "bg-emerald-500/12 text-emerald-700 dark:text-emerald-300"
                            : helper.availability === "busy"
                                ? "bg-amber-500/12 text-amber-800 dark:text-amber-300"
                                : "bg-slate-500/12 text-slate-600 dark:text-slate-300"
                    }`}>
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            helper.availability === "available" ? "bg-emerald-500"
                                : helper.availability === "busy" ? "bg-amber-500"
                                    : "bg-slate-500"
                        }`} />
                        {t(`community.avail_${helper.availability}`)}
                    </p>
                )}
                {typeof helper.rescue_count === "number" && helper.rescue_count > 0 && (
                    <p className="mt-1 text-[11px] text-paw-forest dark:text-paw-terra font-semibold">
                        🏅 {t("community.rescue_count", { n: helper.rescue_count })}
                    </p>
                )}
                <p className="mt-3 text-[11px] text-paw-forest dark:text-paw-terra font-semibold">
                    🛡️ {t("community.helper_ready_h")}
                </p>
                <p className="mt-1 text-[11px] text-paw-ink2/70 dark:text-paw-bone/50 leading-snug">
                    {t("community.helper_ready_sub")}
                </p>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="community-helper-sheet-close"
                    className="mt-4 w-full rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-paw-terra dark:hover:bg-paw-terraDark text-white text-sm font-semibold py-2.5 transition"
                >
                    {t("common.close")}
                </button>
            </motion.div>
        </motion.div>
    );
}

/* =========================================================
 *  MAIN
 * =======================================================*/
export default function CommunityMap() {
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const isStoreApp = useIsStoreApp();
    const isFreeUser = user?.plan !== "premium";
    const openPremiumGate = useHandlePremiumGate(user);
    // Freemium gate for the PawLink Rescue Map. Enforced for ALL free users
    // AND for the "user still loading" window (fail-closed — never leak the
    // map to a fetch-in-flight user):
    //   • Free store-app user → coming-soon modal + stays on /home
    //   • Free web user       → redirected to /subscription (Paddle upgrade)
    //   • Not-yet-loaded user → treated as gated until AuthContext resolves
    //   • Real Premium user   → passes through, full map renders.
    const authLoading = user === null;
    const gated = authLoading;    // Free users no longer blocked from the map itself.

    const [settings, setSettings] = useState(null);
    const [data, setData] = useState({ helpers: [], lost_pets: [], recent_found: [], center: null, neighborhood: null, city: null, helper_count: 0, city_guardians: 0, radius_km: 5 });
    // Local Walks integration — same map, new layer.
    // walksPins is populated only when the caller has geolocation permission.
    // walksActive tracks the caller's own live walk (start / end control).
    const [walksPins, setWalksPins] = useState([]);
    const [walksCount, setWalksCount] = useState(null);
    const [walksActive, setWalksActive] = useState(null);
    const [walksBusy, setWalksBusy] = useState(false);
    const [walksError, setWalksError] = useState("");
    const [showWalkStart, setShowWalkStart] = useState(false);
    const [walkPets, setWalkPets] = useState([]);
    const [walkPetId, setWalkPetId] = useState("");
    const [walkDuration, setWalkDuration] = useState(30);
    const [walkConsent, setWalkConsent] = useState(false);
    const [searchParams] = useSearchParams();
    // Preselect the URL-provided filter (e.g. /community?filter=walking_now).
    const [filter, setFilter] = useState(searchParams.get("filter") || "all");
    const [radiusKm, setRadiusKm] = useState(5);
    const [loading, setLoading] = useState(true);
    const [locating, setLocating] = useState(false);
    const [visError, setVisError] = useState("");
    const [selectedPet, setSelectedPet] = useState(null); // { pet, kind }
    const [selectedHelper, setSelectedHelper] = useState(null);

    // Live browser geolocation — used ONLY to center the map on this device.
    // Never persisted to the DB (that's `settings.latitude/longitude` via
    // `shareApproxLocation`). If permission is denied we fall back to the
    // last approved area (data.center) or the language regional overview.
    const [liveCenter, setLiveCenter] = useState(null);
    const [liveDenied, setLiveDenied] = useState(false);
    const requestLiveLocation = () => {
        if (!navigator.geolocation) { setLiveDenied(true); return; }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLiveCenter({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                setLiveDenied(false);
            },
            () => { setLiveDenied(true); },
            { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 },
        );
    };
    // Auto-request live location once auth resolves.
    useEffect(() => {
        if (authLoading) return;
        requestLiveLocation();
    }, [authLoading]);

    useEffect(() => {
        if (authLoading) return;                            // wait for AuthContext
        // Free users are allowed onto the map (Walking Now works for Free).
        // Rescue-map pins remain Protection-only via server-side gates; the
        // pricing upsell now surfaces contextually on the pin panel, not
        // via a full-page redirect. Auto-open the paywall ONLY if the URL
        // is explicitly requesting a Protection-only filter that we cannot
        // fulfil for a Free user (helpers / found / all rescue data).
        if (isFreeUser && (filter === "helpers" || filter === "found")) {
            openPremiumGate("community_map");
        }
    }, [authLoading, isFreeUser, openPremiumGate, filter]);

    const load = async (r = radiusKm) => {
        try {
            const [s, n] = await Promise.all([
                api.get("/community/me"),
                api.get(`/community/nearby?radius_km=${r}`),
            ]);
            setSettings(s.data); setData(n.data);
        } catch (_) { /* ignore */ }
        setLoading(false);
    };
    // Fetch nearby data ONLY after auth has resolved AND the user is
    // Premium. Prevents any protected request from being emitted for a
    // free / unauthenticated / not-yet-loaded user, even though the
    // backend also refuses those requests with HTTP 402 / 401.
    useEffect(() => {
        if (authLoading) return;
        if (isFreeUser) { setLoading(false); return; }   // Free: skip protected calls, still render map
        load(radiusKm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isFreeUser, radiusKm]);

    // Load caller's active walk + owned pets (so Start Walk modal knows which
    // pet to walk). Runs for both Free and Protection so the start/end
    // control works for Free too.
    useEffect(() => {
        if (authLoading) return;
        (async () => {
            try {
                const [w, p] = await Promise.all([
                    api.get("/walks/active"),
                    api.get("/pets"),
                ]);
                const pets = p.data || [];
                setWalkPets(pets);
                if (pets[0]?.id) setWalkPetId(pets[0].id);
                if (w.data.active) {
                    const petName = (pets.find((x) => x.id === w.data.active.pet_id) || {}).name || null;
                    setWalksActive({ ...w.data.active, pet_name: petName });
                } else {
                    setWalksActive(null);
                }
            } catch { /* ignore */ }
        })();
    }, [authLoading]);

    // Poll nearby walks every 30s. Uses browser geolocation. Free users
    // get only the aggregate count; Protection users get pin details.
    useEffect(() => {
        if (authLoading || isFreeUser) {
            // Free-tier: still fetch count (server enforces free-vs-protection).
            if (authLoading) return;
        }
        let stopped = false;
        const tick = async () => {
            if (stopped) return;
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(async (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                try {
                    const { data: c } = await api.get(`/walks/nearby-count?lat=${lat}&lng=${lng}&radius_km=${radiusKm}`);
                    if (!stopped) setWalksCount(c.count);
                } catch { /* ignore */ }
                if (!isFreeUser) {
                    try {
                        const { data: p } = await api.get(`/walks/nearby-pins?lat=${lat}&lng=${lng}&radius_km=${radiusKm}`);
                        if (!stopped) setWalksPins(p.pins || []);
                    } catch { /* ignore */ }
                }
            }, () => { /* no location perm — ignore */ }, { maximumAge: 60000, timeout: 6000 });
        };
        tick();
        const id = setInterval(tick, 30000);
        return () => { stopped = true; clearInterval(id); };
    }, [authLoading, isFreeUser, radiusKm]);

    const startWalk = async () => {
        if (walksBusy) return;
        setWalksBusy(true); setWalksError("");
        try {
            const pos = await new Promise((res, rej) =>
                navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: false, maximumAge: 60000, timeout: 8000 }));
            const { latitude: lat, longitude: lng } = pos.coords;
            const { data } = await api.post("/walks/start", {
                pet_id: walkPetId, duration_min: walkDuration, lat, lng, consent: true,
            });
            setWalksActive({
                id: data.id,
                pet_id: walkPetId,
                pet_name: (walkPets.find((p) => p.id === walkPetId) || {}).name || null,
                pet_first_name: data.pet_first_name || null,
                pet_photo: data.pet_photo || null,
                lat_fuzzed: data.lat_fuzzed,
                lng_fuzzed: data.lng_fuzzed,
                duration_min: walkDuration,
                expires_at: data.expires_at,
                remaining_seconds: data.remaining_seconds,
            });
            setShowWalkStart(false); setWalkConsent(false);
        } catch (e) {
            setWalksError(e?.response?.data?.detail || t("walks.err_start"));
        } finally { setWalksBusy(false); }
    };
    const endWalk = async () => {
        if (!walksActive?.id) return;
        setWalksBusy(true);
        try { await api.post(`/walks/${walksActive.id}/end`); setWalksActive(null); }
        catch { /* ignore */ }
        finally { setWalksBusy(false); }
    };

    // Rules-of-Hooks safe: null-render AFTER all top-level hooks are called.
    // On a mount that triggers the gate, the effect above navigates the
    // free user away (modal on store-app / /subscription on web) — this
    // just prevents the map body from painting while the redirect happens.
    if (gated) return null;

    const shareApproxLocation = () => {
        if (!navigator.geolocation) { setVisError(t("community.geo_unsupported")); return; }
        setLocating(true); setVisError("");
        navigator.geolocation.getCurrentPosition(async (pos) => {
            try {
                await api.patch("/community/settings", {
                    latitude: pos.coords.latitude, longitude: pos.coords.longitude,
                    visibility: settings?.visibility === "invisible" ? "approximate" : (settings?.visibility || "approximate"),
                });
                await load();
            } catch (_) { setVisError(t("community.location_error")); }
            finally { setLocating(false); }
        }, () => { setVisError(t("community.location_denied")); setLocating(false); }, { timeout: 10000, enableHighAccuracy: false });
    };

    // Inline visibility change from the compact status row on the map page.
    // Persists via the existing PATCH /community/settings endpoint (same one
    // Account → Privacy uses) and reloads the visibility from the server so
    // the value survives a full-page refresh.
    const changeVisibility = async (nextVisibility) => {
        try {
            await api.patch("/community/settings", { visibility: nextVisibility });
            await load();
        } catch (_) { /* ignore — state remains previous value */ }
    };

    // Filters and derived flags are computed inside <MapView />.

    return (
        <PageShell testId="community-page" title={t("community.title")} sub={t("community.sub")}>
            <style>{`
                /* ------- Warm pet-friendly tile filter ------- */
                .pl-map .leaflet-tile-pane { filter: saturate(1.14) hue-rotate(-6deg) brightness(1.02); }
                .leaflet-container { border-radius: 24px; font-family: inherit; background: #fbf6ee; }
                .leaflet-container:focus, .leaflet-container:focus-visible { outline: none; }
                .leaflet-control-attribution { font-size: 9px !important; background: rgba(255,255,255,0.7) !important; backdrop-filter: blur(6px); }

                /* ------- Avatar pins (Snap-Map style) ------- */
                .pl-pin-wrap { pointer-events: auto; }
                .pl-avatar-pin {
                    position: relative;
                    border-radius: 999px;
                    transform: translateY(0);
                    transition: transform 220ms cubic-bezier(.2,.9,.3,1.2);
                    cursor: pointer;
                }
                .pl-avatar-pin:hover { transform: translateY(-3px) scale(1.08); }
                .pl-avatar-outline {
                    position: absolute; inset: -3px;
                    border-radius: 999px;
                    border: 3px solid #10b981;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.22), 0 0 0 3px white;
                    z-index: 2;
                    pointer-events: none;
                }
                .pl-avatar-img {
                    width: 100%; height: 100%;
                    object-fit: cover;
                    border-radius: 999px;
                    display: block;
                    background: #eee;
                }
                .pl-avatar-pulse {
                    position: absolute;
                    inset: -12px;
                    border-radius: 999px;
                    z-index: 1;
                    animation: pl-ping 1.6s cubic-bezier(0,0,.2,1) infinite;
                    pointer-events: none;
                }
                .pl-avatar-badge {
                    position: absolute;
                    bottom: -6px; inset-inline-end: -6px;
                    min-width: 22px; height: 22px;
                    padding: 0 6px;
                    border-radius: 999px;
                    color: white; font-size: 11px; font-weight: 700;
                    display: grid; place-items: center;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.25), 0 0 0 2px white;
                    z-index: 3;
                }

                /* ------- Self dot ------- */
                .pl-self-dot { position: relative; width: 30px; height: 30px; display: grid; place-items: center; }
                .pl-self-core {
                    position: relative; z-index: 2;
                    width: 16px; height: 16px;
                    border-radius: 999px;
                    background: radial-gradient(circle at 30% 30%, #FF8A00, #E85F1E);
                    border: 3px solid white;
                    box-shadow: 0 0 0 4px rgba(248, 122, 42, 0.18);
                }
                .pl-self-halo {
                    position: absolute; inset: 0;
                    border-radius: 999px;
                    background: rgba(86, 166, 126, 0.35);
                    animation: pl-halo 2.6s ease-out infinite;
                }

                @keyframes pl-ping {
                    0%   { transform: scale(0.5); opacity: 1; }
                    75%  { transform: scale(1.8); opacity: 0; }
                    100% { transform: scale(1.8); opacity: 0; }
                }
                @keyframes pl-halo {
                    0%,100% { transform: scale(0.9); opacity: 0.6; }
                    50%     { transform: scale(1.35); opacity: 0.15; }
                }
                @keyframes pl-live-pulse {
                    0%,100% { transform: scale(1);   opacity: 1; }
                    50%     { transform: scale(1.4); opacity: 0.55; }
                }

                /* ------- Live pulse dot (LIVE chip) ------- */
                .pl-live-dot {
                    display: inline-block;
                    width: 8px; height: 8px;
                    border-radius: 999px;
                    background: #ef4444;
                    animation: pl-live-pulse 1.4s ease-in-out infinite;
                    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.25);
                }

                /* ------- Paw watermark backdrop ------- */
                .pl-paw-bg {
                    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><g fill='%231e3a2f' opacity='0.045'><ellipse cx='30' cy='38' rx='6' ry='8'/><ellipse cx='50' cy='30' rx='6' ry='8'/><ellipse cx='70' cy='38' rx='6' ry='8'/><ellipse cx='22' cy='58' rx='5' ry='7'/><ellipse cx='78' cy='58' rx='5' ry='7'/><path d='M50 55 Q35 55 32 72 Q30 88 50 88 Q70 88 68 72 Q65 55 50 55 Z'/></g></svg>");
                    background-size: 140px 140px;
                    background-position: right -20px bottom -20px;
                    background-repeat: no-repeat;
                }
                .pl-paw-bg-2 {
                    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><g fill='%23d76a3a' opacity='0.09'><ellipse cx='30' cy='38' rx='6' ry='8'/><ellipse cx='50' cy='30' rx='6' ry='8'/><ellipse cx='70' cy='38' rx='6' ry='8'/><ellipse cx='22' cy='58' rx='5' ry='7'/><ellipse cx='78' cy='58' rx='5' ry='7'/><path d='M50 55 Q35 55 32 72 Q30 88 50 88 Q70 88 68 72 Q65 55 50 55 Z'/></g></svg>");
                    background-size: 120px 120px;
                    background-position: left -10px top -20px, right -20px bottom -30px;
                    background-repeat: no-repeat;
                }

                /* ------- Selected filter — warm brand gradient ------- */
                .pl-filter-active {
                    background: linear-gradient(135deg, #F87A2A 0%, #E85F1E 100%) !important;
                    color: white !important;
                    box-shadow: 0 6px 18px -6px rgba(248, 122, 42, 0.55), 0 0 0 1px rgba(255, 138, 42, 0.35) inset;
                }

                /* ------- Popup styling ------- */
                .leaflet-popup-content-wrapper {
                    border-radius: 18px;
                    box-shadow: 0 20px 40px -12px rgba(0,0,0,0.28);
                }
                .leaflet-popup-content { margin: 12px 14px !important; font-family: inherit; }

                /* ------- Cluster styling ------- */
                .pl-cluster-wrap { pointer-events: auto; }
                .pl-cluster {
                    position: relative;
                    border-radius: 999px;
                    display: grid; place-items: center;
                    cursor: pointer;
                    transition: transform 220ms cubic-bezier(.2,.9,.3,1.2);
                }
                .pl-cluster:hover { transform: scale(1.06); }
                .pl-cluster-inner {
                    position: relative; z-index: 2;
                    width: 100%; height: 100%;
                    display: grid; place-items: center;
                    border-radius: 999px;
                    background: radial-gradient(circle at 30% 25%, #FF8A00 0%, #E85F1E 60%, #8a3c14 200%);
                    color: white;
                    box-shadow: 0 6px 16px rgba(30, 58, 47, 0.35), 0 0 0 3px white, 0 0 0 4px rgba(30, 58, 47, 0.15);
                    text-align: center;
                    line-height: 1;
                }
                .pl-cluster-num {
                    font-family: "Fraunces", "Playfair Display", serif;
                    font-size: 18px; font-weight: 600;
                    letter-spacing: -0.02em;
                }
                .pl-cluster-label {
                    font-size: 8px; font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    opacity: 0.85;
                    margin-top: 1px;
                }
                .pl-cluster-pulse {
                    position: absolute; inset: -6px;
                    border-radius: 999px;
                    z-index: 1;
                    background: rgba(248, 122, 42, 0.28);
                    animation: pl-halo 3.2s ease-out infinite;
                    pointer-events: none;
                }
                .leaflet-cluster-anim .leaflet-marker-icon,
                .leaflet-cluster-anim .leaflet-marker-shadow { transition: transform 0.35s ease-out, opacity 0.35s ease-in; }
            `}</style>

            {loading ? (
                <p className="text-paw-ink2 dark:text-paw-bone/60" data-testid="community-loading">{t("dash.loading")}</p>
            ) : (
                // Merge the caller's OWN active walk into walksPins so their
                // orange paw marker also appears on the map. /walks/nearby-pins
                // explicitly excludes self, and it's Protection-only, so Free
                // walkers would otherwise never see their own pin. Nothing
                // private is exposed — we use the already-fuzzed lat/lng
                // that the backend returned.
                <MapView
                    t={t}
                    lang={lang}
                    data={data}
                    settings={settings}
                    filter={filter}
                    setFilter={setFilter}
                    radiusKm={radiusKm}
                    setRadiusKm={setRadiusKm}
                    shareApproxLocation={shareApproxLocation}
                    locating={locating}
                    onSelectPet={setSelectedPet}
                    onSelectHelper={setSelectedHelper}
                    walksActive={walksActive}
                    walksPins={(() => {
                        const others = walksPins || [];
                        if (!walksActive || walksActive.lat_fuzzed == null || walksActive.lng_fuzzed == null) return others;
                        // Skip if the same walk is already in the list (shouldn't happen, but guard).
                        if (others.some((p) => p.id === walksActive.id)) return others;
                        const now = Date.now();
                        const remaining = Math.max(0, Math.floor((new Date(walksActive.expires_at).getTime() - now) / 1000));
                        const own = {
                            id: walksActive.id,
                            pet_first_name: walksActive.pet_name || walksActive.pet_first_name || "",
                            pet_photo: walksActive.pet_photo || null,
                            lat: walksActive.lat_fuzzed,
                            lng: walksActive.lng_fuzzed,
                            remaining_seconds: remaining,
                            duration_min: walksActive.duration_min,
                            approx_distance_km: 0,
                        };
                        return [own, ...others];
                    })()}
                    walksCount={walksCount}
                    walksError={walksError}
                    onOpenStart={() => setShowWalkStart(true)}
                    onEndWalk={endWalk}
                    walksBusy={walksBusy}
                    liveCenter={liveCenter}
                    liveDenied={liveDenied}
                    requestLiveLocation={requestLiveLocation}
                    onChangeVisibility={changeVisibility}
                />
            )}

            <AnimatePresence>
                {selectedPet && (
                    <PetSheet pet={selectedPet.pet} kind={selectedPet.kind} t={t} onClose={() => setSelectedPet(null)} />
                )}
                {selectedHelper && (
                    <HelperSheet helper={selectedHelper} t={t} onClose={() => setSelectedHelper(null)} />
                )}
                {showWalkStart && (
                    <WalkStartModal
                        t={t}
                        pets={walkPets}
                        petId={walkPetId} setPetId={setWalkPetId}
                        duration={walkDuration} setDuration={setWalkDuration}
                        consent={walkConsent} setConsent={setWalkConsent}
                        busy={walksBusy} onCancel={() => setShowWalkStart(false)}
                        onStart={startWalk} error={walksError}
                        hasLocation={Boolean(liveCenter)}
                    />
                )}
            </AnimatePresence>
        </PageShell>
    );
}

/* =========================================================
 *  MAP VIEW — landscape hero layout with clustering
 * =======================================================*/
function MapView({ t, lang, data, settings, filter, setFilter, radiusKm, setRadiusKm, shareApproxLocation, locating, onSelectPet, onSelectHelper, walksActive, walksPins, walksCount, walksError, onOpenStart, onEndWalk, walksBusy, liveCenter, liveDenied, requestLiveLocation, onChangeVisibility }) {
    const showWalks = filter === "all" || filter === "walking_now";

    // Center-resolution priority:
    //   1. Live browser location (only for centering; not stored)
    //   2. Approved area from the DB (settings.approximate area)
    //   3. Language regional fallback — city-level zoom, NEVER world level
    const hasSharedLocation = Boolean(data.center);
    const fallback = getFallbackCenter(lang);
    const resolvedUserCenter =
        liveCenter ? { latitude: liveCenter.latitude, longitude: liveCenter.longitude }
        : (data.center || null);
    const initialCenter = resolvedUserCenter
        ? { latitude: resolvedUserCenter.latitude, longitude: resolvedUserCenter.longitude }
        : { latitude: fallback.latitude, longitude: fallback.longitude };
    const initialZoom = resolvedUserCenter ? 15 : fallback.zoom;
    // Suppress noise: walksPins is consumed inside the map component below.
    void walksPins;

    return (
        <div className="space-y-4">
            {/* 1) Filters + distance + walking control — the ONLY control row */}
            <FiltersBar
                t={t}
                filter={filter}
                setFilter={setFilter}
                radiusKm={radiusKm}
                setRadiusKm={setRadiusKm}
                showDistance={Boolean(resolvedUserCenter)}
                walksActive={walksActive}
                walksBusy={walksBusy}
                walksError={walksError}
                onOpenStart={onOpenStart}
                onEndWalk={onEndWalk}
            />

            {/* 2) The Google Map — untouched behaviour */}
            <div className="relative">
                <div
                    className="pl-map rounded-3xl overflow-hidden border-2 border-paw-forest/20 dark:border-paw-terra/25 shadow-hover relative"
                    data-testid="community-map"
                >
                    {/* Location prompt — appears ONLY when the user has neither
                        a live browser location NOR an approved area. Disappears
                        the moment either becomes available. */}
                    {!hasSharedLocation && !liveCenter && (
                        <div
                            className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] w-[calc(100%-1.5rem)] sm:w-auto sm:max-w-md pointer-events-none"
                            data-testid="community-add-area-banner"
                        >
                            <div className="pointer-events-auto flex items-center gap-2.5 rounded-full bg-white/95 dark:bg-[hsl(152,15%,10%)]/95 backdrop-blur-md border border-paw-line dark:border-white/12 shadow-hover px-3 py-2">
                                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-paw-terra to-paw-terraDark text-white grid place-items-center flex-shrink-0">
                                    <Compass size={16} weight="fill" />
                                </span>
                                <p className="text-xs sm:text-[13px] text-paw-ink dark:text-paw-bone leading-tight flex-1 min-w-0 truncate">
                                    {t("community.empty_needs_location_h")}
                                </p>
                                <button
                                    type="button"
                                    onClick={shareApproxLocation}
                                    disabled={locating}
                                    data-testid="community-banner-share-location"
                                    className="rounded-full bg-gradient-to-br from-paw-terra to-paw-terraDark hover:brightness-110 disabled:opacity-70 text-white text-[11px] sm:text-xs font-semibold px-3 py-1.5 shadow-soft transition whitespace-nowrap flex-shrink-0"
                                >
                                    {locating ? t("community.locating") : t("community.empty_share_cta")}
                                </button>
                            </div>
                        </div>
                    )}

                    <GoogleCommunityMapView
                        initialCenter={initialCenter}
                        initialZoom={initialZoom}
                        userCenter={resolvedUserCenter}
                        helpers={data.helpers}
                        lostPets={data.lost_pets}
                        recentFound={data.recent_found}
                        walks={showWalks ? (walksPins || []) : []}
                        filter={filter}
                        onSelectPet={onSelectPet}
                        onSelectHelper={onSelectHelper}
                        t={t}
                    />

                    {/* Floating Center-on-me / Find my location control */}
                    <button
                        type="button"
                        onClick={requestLiveLocation}
                        data-testid="community-find-me"
                        aria-label={t("community.find_my_location")}
                        className="absolute bottom-4 end-4 z-[500] inline-flex items-center gap-1.5 rounded-full bg-white/95 dark:bg-[hsl(152,15%,10%)]/95 backdrop-blur px-3.5 h-10 text-xs font-semibold text-paw-ink dark:text-paw-bone shadow-hover border border-paw-line dark:border-white/10 hover:bg-white transition"
                    >
                        <Crosshair size={14} weight="bold" className="text-paw-forest dark:text-paw-terra" />
                        {liveDenied ? t("community.find_my_location") : t("community.center_on_me")}
                    </button>
                </div>
            </div>

            {/* 3) Compact map-visibility row — one line, no big card */}
            <VisibilityRow t={t} settings={settings} walksActive={walksActive} onChangeVisibility={onChangeVisibility} />
        </div>
    );
}

/* =========================================================
 *  FILTERS BAR — the ONE control row above the map. Renders:
 *    • 4 filter chips (All · Lost Pets · Walking Now · Nearby)
 *      that always change the map markers.
 *    • A compact "Distance: N km ▾" native select (only shown
 *      once we have a user location or approved area).
 *    • ONE walking action — Start Walk, OR (during an active
 *      walk) a compact bar with pet name, remaining time, End Walk.
 *  On mobile, the filter chips scroll horizontally; RTL-safe.
 * =======================================================*/
function FiltersBar({ t, filter, setFilter, radiusKm, setRadiusKm, showDistance, walksActive, walksBusy, walksError, onOpenStart, onEndWalk }) {
    // Live countdown for the active walk (1s tick).
    const [, force] = useState(0);
    useEffect(() => {
        if (!walksActive) return;
        const id = setInterval(() => force((x) => x + 1), 1000);
        return () => clearInterval(id);
    }, [walksActive]);
    const remainingSec = walksActive
        ? Math.max(0, Math.floor((new Date(walksActive.expires_at).getTime() - Date.now()) / 1000))
        : 0;
    const mmss = `${Math.floor(remainingSec / 60)}:${String(remainingSec % 60).padStart(2, "0")}`;
    const activePetName = walksActive?.pet_name || t("walks.your_pet");

    const chips = [
        { key: "all",         labelKey: "community.filter_all" },
        { key: "lost",        labelKey: "community.filter_lost" },
        { key: "walking_now", labelKey: "community.filter_walking_now" },
        { key: "helpers",     labelKey: "community.filter_helpers" },
    ];

    return (
        <div className="space-y-2" data-testid="community-controls">
            {/* Filter chips — horizontally scrollable on mobile */}
            <div
                className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1"
                data-testid="community-filters"
                role="tablist"
            >
                {chips.map((f) => (
                    <button
                        key={f.key} type="button" onClick={() => setFilter(f.key)}
                        role="tab"
                        aria-selected={filter === f.key}
                        data-testid={`community-filter-${f.key}`}
                        className={`shrink-0 inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap transition ${
                            filter === f.key
                                ? "bg-paw-terra text-white shadow-soft"
                                : "text-paw-ink dark:text-paw-bone bg-white dark:bg-white/[0.05] border border-paw-line dark:border-white/10 hover:bg-paw-muted dark:hover:bg-white/[0.08]"
                        }`}
                    >
                        {t(f.labelKey)}
                    </button>
                ))}
            </div>

            {/* Distance + Walking control row */}
            <div className="flex items-center gap-2 flex-wrap">
                {showDistance && (
                    <label
                        className="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-white/[0.05] border border-paw-line dark:border-white/10 px-3 h-9 text-[12px] font-semibold text-paw-ink dark:text-paw-bone"
                        data-testid="community-distance-wrap"
                    >
                        <span className="text-paw-ink2 dark:text-paw-bone/60">{t("community.distance_h")}:</span>
                        <select
                            value={radiusKm}
                            onChange={(e) => setRadiusKm(Number(e.target.value))}
                            data-testid="community-distance-select"
                            aria-label={t("community.distance_h")}
                            className="bg-transparent outline-none text-paw-ink dark:text-paw-bone font-semibold pe-0.5"
                        >
                            {[1, 3, 5, 10].map((r) => (
                                <option key={r} value={r}>{r} {t("community.km_unit") || "km"}</option>
                            ))}
                        </select>
                    </label>
                )}

                <div className="ms-auto flex items-center gap-2 flex-wrap">
                    {walksActive ? (
                        <div
                            className="inline-flex items-center gap-2 rounded-full bg-paw-terra/10 border border-paw-terra/40 px-3 h-9"
                            data-testid="walks-active-bar"
                        >
                            <span className="text-[12px] font-semibold text-paw-ink dark:text-paw-bone truncate max-w-[8rem]">
                                🐾 {activePetName}
                            </span>
                            <span data-testid="walks-active-remaining" className="text-[12px] tabular-nums text-paw-ink2 dark:text-paw-bone/70" dir="ltr">
                                ⏱ {mmss}
                            </span>
                            <button
                                type="button" onClick={onEndWalk} disabled={walksBusy}
                                data-testid="walks-end-btn"
                                className="rounded-full bg-paw-ink text-white dark:bg-white dark:text-paw-ink px-3 h-7 text-[12px] font-semibold disabled:opacity-60"
                            >
                                {t("walks.end")}
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button" onClick={onOpenStart}
                            data-testid="walks-start-btn"
                            className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-4 h-9 text-[13px] font-bold shadow-soft"
                        >
                            {t("walks.cta_start")}
                        </button>
                    )}
                </div>
            </div>

            {walksError && (
                <p data-testid="walks-error" className="text-[11px] text-red-600">{walksError}</p>
            )}
        </div>
    );
}

/* =========================================================
 *  VISIBILITY ROW — compact one-line status "Map visibility:
 *  Hidden/Approximate — Change" that deep-links to
 *  Account → Privacy where the full setting lives. No large
 *  paragraph, no exact location exposure.
 * =======================================================*/
function VisibilityRow({ t, settings, walksActive, onChangeVisibility }) {
    const [open, setOpen] = useState(false);
    // Backend states: "invisible" | "approximate" | "helper".
    //   • "invisible"                       → Hidden
    //   • "approximate" + no active walk    → Visible approximately
    //   • "approximate" + active walk       → Live approximately (display-only)
    //
    // The selector ONLY offers Hidden / Visible approximately as
    // user-selectable preferences. "Live approximately" is a derived
    // status that the compact row auto-shows for the duration of an
    // active walk; the moment the walk ends, `walksActive` becomes
    // null and the status reverts to whatever the user had chosen
    // (Hidden or Visible approximately) — nothing is written to the
    // DB during a walk, so there is nothing to "restore".
    const raw = settings?.visibility === "invisible" || !settings?.visibility ? "invisible" : "approximate";
    const currentLabel = raw === "invisible"
        ? t("community.map_visibility_hidden")
        : (walksActive
            ? t("community.map_visibility_live_approx")
            : t("community.map_visibility_approximate"));

    const options = [
        { key: "invisible",   label: t("community.map_visibility_hidden") },
        { key: "approximate", label: t("community.map_visibility_approximate") },
    ];

    const pick = async (key) => {
        await onChangeVisibility(key);
        setOpen(false);
    };

    return (
        <div
            data-testid="community-visibility-row"
            className="rounded-2xl border border-paw-line dark:border-white/10 bg-white/85 dark:bg-white/[0.04] backdrop-blur"
        >
            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-paw-forest text-white grid place-items-center flex-shrink-0">
                    <ShieldCheck size={14} weight="fill" />
                </span>
                <p className="text-[13px] text-paw-ink dark:text-paw-bone flex-1 min-w-0 truncate">
                    <span className="text-paw-ink2 dark:text-paw-bone/60">{t("community.map_visibility_h")}:</span>{" "}
                    <span data-testid="community-visibility-value" className="font-semibold">{currentLabel}</span>
                </p>
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    data-testid="community-visibility-change"
                    className="rounded-full border border-paw-line dark:border-white/15 text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5 text-[12px] font-semibold px-3 h-8 transition"
                >
                    {t("community.map_visibility_change")}
                </button>
            </div>
            {open && (
                <div
                    data-testid="community-visibility-selector"
                    className="border-t border-paw-line dark:border-white/10 px-3.5 py-2.5 grid gap-1.5"
                    role="radiogroup"
                >
                    {options.map((opt) => {
                        // Highlight the option currently in effect. During an
                        // active walk the compact label reads "Live
                        // approximately" but the underlying preference is
                        // still "approximate" — keep that row highlighted so
                        // the user can see (and change if they want) what
                        // will apply once the walk ends.
                        const isActive = raw === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                role="radio"
                                aria-checked={isActive}
                                onClick={() => pick(opt.key)}
                                data-testid={`community-visibility-option-${opt.key}`}
                                className={`text-start rounded-xl border px-3 py-2 text-[13px] font-semibold transition ${
                                    isActive
                                        ? "border-transparent bg-paw-terra text-white shadow-soft"
                                        : "border-paw-line dark:border-white/10 bg-white dark:bg-white/[0.03] text-paw-ink dark:text-paw-bone hover:bg-paw-muted dark:hover:bg-white/5"
                                }`}
                            >
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* Pet+duration+consent modal. Now surfaces:
 *   • empty state ("Create a pet profile") when the account has no pets
 *   • auto-selects the sole pet when there is exactly one
 *   • disables Start Walk until pet, duration, consent AND live browser
 *     location are all valid — never a mysterious grey button.
 */
function WalkStartModal({ t, pets, petId, setPetId, duration, setDuration, consent, setConsent, busy, onCancel, onStart, error, hasLocation }) {
    const navigate = useNavigate();
    const list = pets || [];
    const isEmpty = list.length === 0;

    // If exactly one pet, ensure it's selected. (Parent also does this on
    // load; this keeps the modal correct if the pet list changes while the
    // modal is already open.)
    useEffect(() => {
        if (list.length === 1 && list[0].id && petId !== list[0].id) {
            setPetId(list[0].id);
        }
    }, [list, petId, setPetId]);

    const missing = [];
    if (!petId) missing.push(t("walks.missing_pet"));
    if (!duration) missing.push(t("walks.missing_duration"));
    if (!consent) missing.push(t("walks.missing_consent"));
    if (!hasLocation) missing.push(t("walks.missing_location"));
    const canStart = !isEmpty && missing.length === 0 && !busy;

    return (
        <motion.div
            key="walk-start-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center p-3 sm:p-6"
        >
            <motion.div
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
                data-testid="walks-start-modal"
                className="w-full sm:max-w-lg rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] border border-paw-line dark:border-white/10 p-5"
            >
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-paw-terra">{t("walks.kicker")}</p>
                <h2 className="mt-1 font-display text-xl">{t("walks.start_title")}</h2>

                {isEmpty ? (
                    <div data-testid="walks-empty-state" className="mt-4 rounded-2xl border border-paw-terra/25 bg-paw-terra/8 p-4 space-y-2.5">
                        <p className="text-[13px] font-semibold text-paw-ink dark:text-paw-bone">
                            {t("walks.need_pet_h")}
                        </p>
                        <p className="text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                            {t("walks.need_pet_sub")}
                        </p>
                        <button
                            type="button"
                            onClick={() => { navigate("/pets/new"); onCancel(); }}
                            data-testid="walks-create-pet"
                            className="inline-flex items-center gap-1.5 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white text-[13px] font-semibold px-4 h-9 shadow-soft"
                        >
                            {t("walks.create_pet_cta")}
                        </button>
                    </div>
                ) : (
                    <label className="mt-4 block text-[12px] text-paw-ink2 dark:text-paw-bone/70">
                        {t("walks.pet")}
                        {list.length === 1 ? (
                            <p
                                data-testid="walks-form-pet-single"
                                className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-paw-muted dark:bg-white/[0.05] px-3 h-10 text-sm font-semibold text-paw-ink dark:text-paw-bone flex items-center"
                            >
                                🐾 {list[0].name}
                            </p>
                        ) : (
                            <select
                                data-testid="walks-form-pet"
                                value={petId} onChange={(e) => setPetId(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-paw-line dark:border-white/15 bg-white dark:bg-white/[0.05] px-2 h-10 text-sm"
                            >
                                {list.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                            </select>
                        )}
                    </label>
                )}

                {!isEmpty && (
                    <>
                        <p className="mt-4 text-[12px] text-paw-ink2 dark:text-paw-bone/70">{t("walks.duration")}</p>
                        <div data-testid="walks-form-duration" className="mt-1.5 grid grid-cols-4 gap-2">
                            {[15, 30, 60, 90].map((d) => (
                                <button
                                    key={d} type="button" onClick={() => setDuration(d)}
                                    data-testid={`walks-duration-${d}`}
                                    className={"rounded-xl border h-10 text-sm font-semibold transition " +
                                        (duration === d ? "bg-paw-terra border-paw-terra text-white" : "border-paw-line dark:border-white/15 hover:bg-paw-terra/8")}
                                >{d} {t("walks.min")}</button>
                            ))}
                        </div>

                        <label className="mt-4 flex items-start gap-2 text-[13px] leading-snug">
                            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                                   data-testid="walks-consent-check" className="mt-0.5 h-4 w-4 accent-paw-terra" />
                            <span data-testid="walks-consent-text">{t("walks.consent_text")}</span>
                        </label>

                        {!hasLocation && (
                            <p data-testid="walks-need-location" className="mt-3 text-[12px] text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
                                {t("walks.missing_location_hint")}
                            </p>
                        )}
                    </>
                )}

                {error && <p data-testid="walks-form-error" className="mt-3 text-[12px] text-red-600 dark:text-red-400">{error}</p>}

                <div className="mt-5 flex justify-end gap-2">
                    <button type="button" onClick={onCancel} data-testid="walks-cancel-btn"
                            className="rounded-full px-4 h-10 text-sm border border-paw-line dark:border-white/15">
                        {t("walks.cancel")}
                    </button>
                    {!isEmpty && (
                        <button type="button" onClick={onStart} disabled={!canStart}
                                data-testid="walks-confirm-start"
                                className="rounded-full bg-paw-terra hover:bg-paw-terraDark text-white px-5 h-10 text-sm font-semibold disabled:opacity-60">
                            {busy ? t("walks.starting") : t("walks.confirm_start")}
                        </button>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}

